import { useState, useCallback } from 'react';
import { getSiteDiscussions } from '../../firebase/siteDiscussions';
import { useToast } from '@chakra-ui/react';

export const useComments = (siteId) => {
  const toast = useToast();
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);

  const fetchComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const result = await getSiteDiscussions(siteId);
      if (result.success) {
        const allDiscussions = result.data;
        
        const discussionsById = new Map();
        allDiscussions.forEach(d => discussionsById.set(d.id, { ...d, replies: [] }));
        
        allDiscussions.forEach(discussion => {
          if (discussion.type === 'reply') {
            const parent = discussionsById.get(discussion.replyTo);
            if (parent) {
              parent.replies = parent.replies || [];
              parent.replies.push(discussion);
            }
          }
        });

        const buildReplyTree = (comment) => {
          if (comment.replies) {
            comment.replies = comment.replies.map(reply => {
              const replyWithNested = discussionsById.get(reply.id);
              return buildReplyTree(replyWithNested);
            });
          }
          return comment;
        };

        allDiscussions.forEach(discussion => {
          if (!discussion.replyTo) {
            buildReplyTree(discussionsById.get(discussion.id));
          }
        });

        const topLevelComments = Array.from(discussionsById.values())
          .filter(d => !d.replyTo)
          .sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0));
        
        const sortReplies = (comment) => {
          if (comment.replies?.length > 0) {
            comment.replies.sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0));
            comment.replies.forEach(sortReplies);
          }
          return comment;
        };
        
        topLevelComments.forEach(sortReplies);
        
        setComments(topLevelComments);
      } else {
        console.error('Error fetching comments:', result.error);
        toast({
          title: 'Failed to load comments',
          description: result.error,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setLoadingComments(false);
    }
  }, [siteId, toast]);

  return {
    comments,
    loadingComments,
    fetchComments,
    setComments
  };
};
